// frontend/src/components/Events/EventsAnnouncements.js
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaBullhorn, FaCalendarAlt, FaShare } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { useAnnouncements } from '../../contexts/AnnouncementsContext';
import { useEvents } from '../../contexts/EventsContext';
import PlaceholderImage from '../../images/7.jpg';
import PageHeader from '../common/PageHeader';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const cardHover = {
  hover: {
    y: -5,
    transition: { duration: 0.2 },
  },
};

const parseTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  if (timestamp._seconds && typeof timestamp._seconds === 'number') {
    const milliseconds = timestamp._seconds * 1000 + Math.floor(timestamp._nanoseconds / 1000000);
    return new Date(milliseconds);
  }
  return new Date(timestamp); // Fallback for string or Date object
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not specified";
  try {
    const date = parseTimestamp(dateString);
    if (isNaN(date.getTime())) {
      console.warn("Invalid date value:", dateString, "Falling back to default");
      return "Date not specified";
    }
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Africa/Johannesburg' };
    return date.toLocaleDateString('en-ZA', options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Date not specified";
  }
};

const EventsAnnouncements = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const category = queryParams.get('category');
  const [activeTab, setActiveTab] = useState(category || 'events');
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const { events } = useEvents();
  const { announcements = [] } = useAnnouncements();

  useEffect(() => {
    const processAnnouncements = () => {
      try {
        const uniqueCategories = ['all', ...new Set(announcements.map(announcement => announcement.targetType).filter(Boolean))];
        const filtered = activeCategory === 'all'
          ? announcements
          : announcements.filter(announcement => announcement.targetType === activeCategory);
        
        setCategories(uniqueCategories);
        setFilteredAnnouncements(filtered);
        setLoading(false);
      } catch (err) {
        console.error('Error processing announcements:', err);
        setError('Failed to load announcements. Please try again later.');
        setLoading(false);
      }
    };

    processAnnouncements();
  }, [announcements, activeCategory]);

  if (loading) {
    return (
      <motion.div
        className="flex justify-center items-center h-64"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
        ></motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="bg-red-50 p-4 rounded-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <p className="text-red-700">{error}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="w-full bg-gray-50 py-8 px-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageHeader
        title="Updates"
        subtitle="Experience God in fullness"
        imageUrl="https://images.unsplash.com/photo-1560420025-9453f02b4751?q=80&w=1001&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        imageAlt="Church Events"
      />

      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <motion.div
          className="flex justify-center my-8"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <motion.div
            className="flex rounded-lg border border-gray-200 bg-white p-1"
            variants={item}
          >
            <motion.button
              className={`px-4 py-2 rounded-md flex items-center ${
                activeTab === 'events' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
              } transition-colors`}
              onClick={() => setActiveTab('events')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaCalendarAlt className="mr-2" />
              Events
            </motion.button>
            <motion.button
              className={`px-4 py-2 rounded-md flex items-center ${
                activeTab === 'announcements' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'
              } transition-colors`}
              onClick={() => setActiveTab('announcements')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaBullhorn className="mr-2" />
              Announcements
            </motion.button>
          </motion.div>
        </motion.div>

        <AnimatePresence mode="wait">
          {/* Events Content */}
          {activeTab === 'events' && (
            <motion.div
              key="events"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {events && events.length > 0 ? (
                events.map((event, index) => (
                  <motion.div
                    key={event.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                    variants={item}
                    custom={index}
                    whileHover="hover"
                    variants={cardHover}
                  >
                    <div className="w-full h-fit bg-gray-200 relative">
                      {event.imageUrl && (
                        <motion.img
                          src={event.imageUrl}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          initial={{ opacity: 0.8 }}
                          whileHover={{ scale: 1.05, opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                        <div className="flex items-center text-gray-600 mb-2">
                          <FaCalendarAlt className="mr-2 text-primary" />
                          <span>{formatDate(event.startDate)} at {event.time || 'N/A'}</span>
                        </div>
                        <p className="text-gray-600">{event.location || 'N/A'}</p>
                      </div>
                      <p className="text-gray-700">{event.description || 'No description available'}</p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div
                  className="col-span-2 text-center py-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-gray-500">No upcoming events at this time.</p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Announcements Content */}
          {activeTab === 'announcements' && (
            <motion.div
              key="announcements"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <motion.div
                className="mb-8 mt-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <motion.button
                      key={category}
                      className={`px-4 py-2 rounded-full ${
                        activeCategory === category
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                      onClick={() => setActiveCategory(category)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {category}
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="bg-white shadow-md rounded-lg overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="p-6">
                  <div className="space-y-6">
                    {filteredAnnouncements && filteredAnnouncements.length > 0 ? (
                      filteredAnnouncements.map((announcement, index) => (
                        <motion.div
                          key={announcement.id}
                          className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="md:w-1/4">
                              <motion.img
                                src={announcement.imageUrl || PlaceholderImage}
                                alt={announcement.title}
                                className="w-full h-48 object-cover rounded-lg shadow-md"
                                whileHover={{ scale: 1.02 }}
                                onError={(e) => {
                                  e.target.src = PlaceholderImage;
                                }}
                              />
                            </div>
                            <div className="md:w-3/4">
                              <div className="flex items-center mb-2">
                                <span className="inline-block bg-primary text-primary-dark text-xs font-medium px-2.5 py-0.5 rounded">
                                  {announcement.targetType || 'General'}
                                </span>
                                <span className="text-gray-500 text-sm ml-3">
                                  {formatDate(announcement.publishedDate)}
                                </span>
                              </div>
                              <h3 className="text-xl font-semibold text-gray-900 mb-2">{announcement.title}</h3>
                              <p className="text-gray-700 mb-4">{announcement.description || 'No description available'}</p>
                              <div className="flex flex-wrap gap-3">
                                <motion.button
                                  className="text-gray-500 hover:text-primary flex items-center"
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <FaShare className="mr-1" />
                                  Share
                                </motion.button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <motion.div
                        className="text-center py-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <p className="text-gray-500">No announcements found at this moment.</p>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default EventsAnnouncements;